/**
 * "Contact page" content — a singleton (like aboutPage/homePage), editable the
 * same way as TemplateServicePage's lead-capture form: per-field text/type/
 * required/options editing via EditableFieldSettings, plus add/remove fields.
 * DEFAULT_PAGE_CONTENT below IS the page's original hardcoded copy
 * (ContactUs.jsx), so the CMS-driven page renders byte-identical to what
 * shipped before.
 */
export const DEFAULT_PAGE_CONTENT = {
  hiddenSections: [],
  hero: {
    pill: 'Contact us',
    title: "Let's start a conversation",
    lead:
      'Whether you are exploring AI automation, custom software, or a strategic partnership, tell us what you need - we read every message and respond as soon as we can.',
    metaPrefix: 'Prefer the consultation flow?',
    metaLinkLabel: 'Book a consultation',
    metaLinkTo: '/book-consultation',
  },
  form: {
    fields: [
      { key: 'name', type: 'text', label: 'Your name', placeholder: 'Jane Doe', required: false },
      { key: 'email', type: 'email', label: 'Your email', placeholder: 'you@company.com', required: true },
      { key: 'phone', type: 'tel', label: 'Your phone number', placeholder: '+1 …', required: false },
      { key: 'subject', type: 'text', label: 'Subject', placeholder: 'What is this about?', required: false, fullWidth: true },
      {
        key: 'service',
        type: 'select',
        label: 'Which service are you interested in?',
        required: false,
        options: [
          { value: 'AI Chatbots', label: 'AI Chatbots' },
          { value: 'AI Workflow Automation', label: 'AI Workflow Automation' },
          { value: 'Machine Learning', label: 'Machine Learning' },
          { value: 'AI Consulting', label: 'AI Consulting' },
          { value: 'Other', label: 'Other' },
        ],
      },
      {
        key: 'budget',
        type: 'select',
        label: 'Budget range',
        required: false,
        options: [
          { value: 'Under $5,000', label: 'Under $5,000' },
          { value: '$5,000 - $15,000', label: '$5,000 - $15,000' },
          { value: '$15,000 - $50,000', label: '$15,000 - $50,000' },
          { value: '$50,000+', label: '$50,000+' },
        ],
      },
      { key: 'message', type: 'textarea', label: 'Message', placeholder: 'Share context, goals, or timelines…', required: false, fullWidth: true },
    ],
    submitLabel: 'Send message',
    successTitle: 'Thank you',
    successText: 'You have submitted the form successfully. We will get in touch with you shortly.',
    successResetLabel: 'Send another message',
  },
};

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function createContactPageTemplate() {
  return deepClone(DEFAULT_PAGE_CONTENT);
}

export function normalizeContactPageData(stored) {
  const tpl = createContactPageTemplate();
  if (!stored || typeof stored !== 'object') return tpl;

  return {
    hiddenSections: Array.isArray(stored.hiddenSections) ? stored.hiddenSections : tpl.hiddenSections,
    hero: { ...tpl.hero, ...(stored.hero || {}) },
    form: {
      ...tpl.form,
      ...(stored.form || {}),
      fields: Array.isArray(stored.form?.fields) && stored.form.fields.length ? stored.form.fields : tpl.form.fields,
    },
  };
}

export function normalizeContactPageStyles(stored) {
  if (!stored || typeof stored !== 'object') return {};
  const out = {};
  for (const [blockId, val] of Object.entries(stored)) {
    if (!val || typeof val !== 'object') continue;
    out[blockId] = {
      desktop: val.desktop && typeof val.desktop === 'object' ? { ...val.desktop } : {},
      mobile: val.mobile && typeof val.mobile === 'object' ? { ...val.mobile } : {},
    };
  }
  return out;
}

/** Full normalized { data, styles } shape, the way it's stored under content.contactPage. */
export function normalizeContactPage(stored) {
  return {
    data: normalizeContactPageData(stored?.data),
    styles: normalizeContactPageStyles(stored?.styles),
    updatedAt: stored?.updatedAt || new Date().toISOString(),
  };
}
